import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import partition from "lodash/partition";
import {
  DreamSearchViewerResult,
  DreamSearchViewerResults,
  Filters,
} from "@portal/app/_common/types/dreamSearch";
import Button from "@mui/material/Button";


import {
  getRotatingTestimonial,
  getTestimonialItemIndexes,
  Testimonial,
} from "../../utils/resultsHelpers";
import { gql } from "graphql-tag";
import { VocationForDreamSearchResultsDocument } from "./generated";

import { VocationIndustryToName } from "@portal/app/_common/types/vocationIndustry";
import { emptyFilters } from "@portal/app/health/_components/utils/useQueryParamFiltersForDreamSearch";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import isEqual from "lodash/isEqual";
import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import { DreamSearchProgramItems } from "./DreamSearchProgramItems";
import { SponsoredResultsDisclaimer } from "./SponsoredResultsDisclaimer";
import { EsyohWidget } from "@portal/app/_components/ui-elements/EsyohWidget";

void gql`
  query vocationForDreamSearchResults($id: ID!) {
    vocation(id: $id) {
      id
      name
    }
  }
`;

export const DreamSearchResults = ({
  items,
  otherRecommendedItems = [],
  eventName,
  filters,
  setFilters,
  clearAllFilters,
  shouldHideTestimonials = false,
  industry,
  pageSize,
  hideSponsoredDisclaimer = false,
  vocationId,
  zip: _zip,
  hideNonPartneredItems = false,
  hideOtherRecommendedItemsWhenPartnered = false,
}: {
  items: DreamSearchViewerResult[];
  otherRecommendedItems?: DreamSearchViewerResults | null;
  eventName: MixpanelEventName;
  filters?: Filters;
  setFilters?: React.Dispatch<React.SetStateAction<Filters>>;
  clearAllFilters?: () => void;
  shouldHideTestimonials?: boolean;
  industry?: GQL.VocationIndustry;
  pageSize: number;
  hideSponsoredDisclaimer?: boolean;
  vocationId?: string;
  zip: string | undefined;
  /** /view SEO pages render non-partnered cards in their own bottom-of-page
   *  section, so suppress them here (branch selection then ignores them). */
  hideNonPartneredItems?: boolean;
  /** /view SEO pages: hide the related-vocation "Other programs you might
   *  like" bucket when exact-match partnered results exist. */
  hideOtherRecommendedItemsWhenPartnered?: boolean;
}) => {
  const isOnlySingleVocationFilter = filters
    ? Object.keys(filters ?? {}).length === 1 && filters["program_vocation_in"]?.length === 1
    : false;
  const { data: vocationData } = useQuery(VocationForDreamSearchResultsDocument, {
    variables: { id: filters?.["program_vocation_in"]?.[0] as string },
    skip: !isOnlySingleVocationFilter,
  });

  const [partneredItems, nonPartneredItems] = partition(items, (item) => item.provider.isPartnered);
  const hasPartneredItems = !!partneredItems.length;
  // With both hide* props false (every page except /view), these reduce to
  // the passed-in props and behavior is unchanged.
  const effectiveOtherRecommendedItems =
    hideOtherRecommendedItemsWhenPartnered && hasPartneredItems ? [] : otherRecommendedItems;
  const mainItems = hideNonPartneredItems ? partneredItems : items;
  const notAvailableItems = hideNonPartneredItems ? [] : nonPartneredItems.slice(0, 10);
  const noResults = mainItems.length === 0 && effectiveOtherRecommendedItems?.length === 0;
  const hasOtherRecommendedItems = !!effectiveOtherRecommendedItems?.length;
  const hasNotAvailableItems = !!notAvailableItems.length;
  const onlyNotAvailableResults =
    hasNotAvailableItems && !hasOtherRecommendedItems && !hasPartneredItems;
  const onlyOtherRecommendedResults =
    mainItems.length === 0 &&
    effectiveOtherRecommendedItems &&
    effectiveOtherRecommendedItems.length > 0;

  const NoResultsAlert = ({
    description: _description,
    hasSimilarPrograms = true,
  }: {
    description: React.ReactNode;
    /** False on the truly-empty page, where claiming to show similar programs would be wrong. */
    hasSimilarPrograms?: boolean;
  }) => {
    const analytics = useMixpanel();
    const { openSignInModal, loggedIn } = useAuthenticatedContext();

    const { program_vocation_in, ...filtersWithoutVocation } = filters ?? {};
    const { program_vocation_in: _, ...emptyFiltersWithoutVocation } = emptyFilters ?? {};
    const areFiltersClearable =
      !!filters &&
      !!setFilters &&
      !!clearAllFilters &&
      !isEqual(filtersWithoutVocation, emptyFiltersWithoutVocation);
    const noMatchesTitle = isOnlySingleVocationFilter
      ? `No exact matches for ${vocationData?.vocation?.name}`
      : "No exact matches";
    return (
      <NoMatchesContainer>
        <NoMatchesTitle>
          {hasSimilarPrograms ? `${noMatchesTitle} — showing similar programs` : noMatchesTitle}
        </NoMatchesTitle>
        <ActionButtons>
          {!loggedIn && (
            <Button
              variant="text"
              size="small"
              onClick={async () => {
                await analytics.track("Dream Search - No Results - Clicked Create Account");
                openSignInModal("CREATE_ACCOUNT");
              }}
            >
              Get notified
            </Button>
          )}
          {areFiltersClearable && (
            <Button
              variant="text"
              size="small"
              onClick={async () => {
                await analytics.track("Dream Search - No Results - Clicked Clear Filters");
                clearAllFilters?.();
              }}
            >
              Clear filters
            </Button>
          )}
        </ActionButtons>
      </NoMatchesContainer>
    );
  };

  /**
   * No results at all
   */
  if (noResults) {
    return (
      <>
        <NoResultsAlert
          description="We couldn't find any results for your search."
          hasSimilarPrograms={false}
        />
        <EsyohWidget vocationId={vocationId} />
      </>
    );
  }

  /**
   * No partnered items, but some other results
   */
  if (!hasPartneredItems) {
    /**
     * Only other recommended items
     */
    if (onlyOtherRecommendedResults) {
      return (
        <>
          <NoResultsAlert
            description={
              <>
                We couldn&apos;t find any results for your search. Here are some other classes that
                might work for you
                {industry ? ` within ${VocationIndustryToName[industry].toLowerCase()}` : ""}.
              </>
            }
          />
          <EsyohWidget vocationId={vocationId} />
          {!hideSponsoredDisclaimer && (
            <Box pb={2}>
              <SponsoredResultsDisclaimer />
            </Box>
          )}
          <DreamSearchResultsContainer data-testid="other-recommended-items">
            <DreamSearchProgramItems items={effectiveOtherRecommendedItems} eventName={eventName} />
          </DreamSearchResultsContainer>
        </>
      );
    }

    /**
     * Only not-available matches
     */
    if (onlyNotAvailableResults) {
      return (
        <>
          <NoResultsAlert description="We couldn't find any partnered results for your search. Some unverified programs in your area may be available." />
          <EsyohWidget vocationId={vocationId} />
          <DreamSearchResultsContainer data-testid="non-partnered-items">
            <DreamSearchProgramItems items={notAvailableItems} eventName={eventName} />
          </DreamSearchResultsContainer>
        </>
      );
    }

    /**
     * Both other recommended, and directory matches
     */
    if (hasOtherRecommendedItems && hasNotAvailableItems) {
      return (
        <>
          <NoResultsAlert description="We found other classes within the industry, or outside Dreambound, that might work for you." />
          {!hideSponsoredDisclaimer && (
            <Box pb={2}>
              <SponsoredResultsDisclaimer />
            </Box>
          )}
          <DreamSearchResultsContainer data-testid="other-recommended-items">
            <DreamSearchProgramItems items={effectiveOtherRecommendedItems} eventName={eventName} />
          </DreamSearchResultsContainer>
          <EsyohWidget vocationId={vocationId} />
          <Typography
            variant="new_h2"
            sx={{
              mb: 2,
              mt: effectiveOtherRecommendedItems.length ? 4 : 0,
            }}
          >
            Programs not available on Dreambound
          </Typography>
          <DreamSearchResultsContainer data-testid="non-partnered-items">
            <DreamSearchProgramItems items={notAvailableItems} eventName={eventName} />
          </DreamSearchResultsContainer>
        </>
      );
    }
  }

  const testimonialItemIndexes = getTestimonialItemIndexes(partneredItems.length, pageSize);
  const partneredItemsMaybeWithTestimonials = !shouldHideTestimonials
    ? partneredItems.reduce<(DreamSearchViewerResult | Testimonial)[]>(
        (acc, item, i) =>
          testimonialItemIndexes.includes(i)
            ? [...acc, getRotatingTestimonial(testimonialItemIndexes.indexOf(i)), item]
            : [...acc, item],
        []
      )
    : partneredItems;

  return (
    <>
      {!hideSponsoredDisclaimer && (
        <Box pb={2}>
          <SponsoredResultsDisclaimer />
        </Box>
      )}
      <DreamSearchResultsContainer data-testid="partnered-items">
        <DreamSearchProgramItems
          items={partneredItemsMaybeWithTestimonials}
          eventName={eventName}
        />
      </DreamSearchResultsContainer>
      {hasOtherRecommendedItems && (
        <>
          <Typography
            variant="new_h2"
            sx={{
              mb: 2,
              mt: (hasOtherRecommendedItems ? effectiveOtherRecommendedItems : partneredItems)
                .length
                ? 4
                : 0,
            }}
          >
            Other programs you might like
          </Typography>
          {!hideSponsoredDisclaimer && (
            <Box pb={2}>
              <SponsoredResultsDisclaimer />
            </Box>
          )}
          <DreamSearchResultsContainer data-testid="other-recommended-items">
            <DreamSearchProgramItems items={effectiveOtherRecommendedItems} eventName={eventName} />
          </DreamSearchResultsContainer>
        </>
      )}
      <EsyohWidget vocationId={vocationId} />
      {hasNotAvailableItems && (
        <>
          <Typography
            variant="new_h2"
            sx={{
              mb: 2,
              mt: (hasOtherRecommendedItems ? effectiveOtherRecommendedItems : partneredItems)
                .length
                ? 4
                : 0,
            }}
          >
            Schools not available on Dreambound
          </Typography>
          <DreamSearchResultsContainer data-testid="non-partnered-items">
            <DreamSearchProgramItems items={notAvailableItems} eventName={eventName} />
          </DreamSearchResultsContainer>
        </>
      )}
    </>
  );
};

const NoMatchesContainer = styled("div")`
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid ${({ theme }) => theme.palette.dream.borderColor};
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0.5rem 0 1rem;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const NoMatchesTitle = styled(Typography)`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const ActionButtons = styled("div")`
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
`;

export const DreamSearchResultsContainer = styled(Box)`
  display: grid;
  grid-gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
  width: 100%;
`;
